import { useEffect, useState, useRef } from "react";
import { Megaphone, Settings, Users, ClipboardList, Loader2, Trash2, Link2, X, CheckCircle2, Search, KeyRound } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { STAGES, can } from "../lib/constants";
import { useAuth } from "../contexts/AuthContext";
import {
  pickupGetAllStageSettings, pickupUpdateStageSettings, pickupSchoolBoard, pickupConfirmDelivery,
  pickupListGuardians, pickupDeleteGuardian, pickupUnlinkStudent, pickupAdminLink, pickupGuardiansStats, pickupResetGuardianPassword,
} from "../lib/api";

const ALL_TABS = [
  { key: "requests", label: "طلبات الاستدعاء", icon: ClipboardList, show: (u) => can.managePickup(u) },
  { key: "guardians", label: "أولياء الأمور", icon: Users, show: (u) => can.manageGuardians(u) },
  { key: "settings", label: "الإعدادات", icon: Settings, show: (u) => can.manageSettingsPickup(u) },
];

export default function PickupAdmin() {
  const { user } = useAuth();
  const TABS = ALL_TABS.filter((t) => t.show(user));
  const [tab, setTab] = useState(TABS[0]?.key || "requests");

  return (
    <div>
      <PageHeader title="إدارة الاستدعاءات" subtitle="التحكم الكامل في نظام استدعاء الطلاب: الطلبات، أولياء الأمور، والإعدادات" icon={Megaphone} />

      <div className="flex gap-2 mb-5 border-b border-slate-200">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-cairo font-bold border-b-2 transition-colors ${
              tab === t.key ? "border-sky-700 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid={`pickup-admin-tab-${t.key}`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "requests" && can.managePickup(user) && <RequestsTab />}
      {tab === "guardians" && can.manageGuardians(user) && <GuardiansTab />}
      {tab === "settings" && can.manageSettingsPickup(user) && <SettingsTab />}
    </div>
  );
}

const STAGE_LABELS = { primary: "المرحلة الابتدائية", middle: "المرحلة المتوسطة", high: "المرحلة الثانوية" };
const DAY_LABELS = { sunday: "الأحد", monday: "الاثنين", tuesday: "الثلاثاء", wednesday: "الأربعاء", thursday: "الخميس" };
const DAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday"];

function SettingsTab() {
  const [allSettings, setAllSettings] = useState(null);
  const [saving, setSaving] = useState({});

  const load = async () => {
    try {
      const data = await pickupGetAllStageSettings();
      const map = {};
      data.forEach((s) => { map[s.stage] = s; });
      setAllSettings(map);
    } catch {
      toast.error("تعذر تحميل الإعدادات");
    }
  };

  useEffect(() => { load(); }, []);

  const updateField = (stage, field, value) => {
    setAllSettings((prev) => ({ ...prev, [stage]: { ...prev[stage], [field]: value } }));
  };

  const updateDayField = (stage, day, field, value) => {
    setAllSettings((prev) => ({
      ...prev,
      [stage]: {
        ...prev[stage],
        days: { ...prev[stage].days, [day]: { ...prev[stage].days[day], [field]: value } },
      },
    }));
  };

  const saveStage = async (stage) => {
    setSaving((s) => ({ ...s, [stage]: true }));
    try {
      const s = allSettings[stage];
      await pickupUpdateStageSettings(stage, {
        cooldown_minutes: Number(s.cooldown_minutes),
        same_student_wait_minutes: Number(s.same_student_wait_minutes),
        late_threshold_minutes: Number(s.late_threshold_minutes),
        auto_deliver_minutes: Number(s.auto_deliver_minutes ?? 7),
        daily_cleanup_time: s.daily_cleanup_time || "17:00",
        days: s.days,
      });
      toast.success(`تم حفظ إعدادات ${STAGE_LABELS[stage]} بنجاح`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحفظ");
    } finally {
      setSaving((sv) => ({ ...sv, [stage]: false }));
    }
  };

  if (!allSettings) return <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-sky-700" /></div>;

  return (
    <div className="space-y-6">
      {["primary", "middle", "high"].map((stage) => {
        const s = allSettings[stage];
        if (!s) return null;
        return (
          <Card key={stage} className="p-5 sm:p-6 border-slate-200">
            <h3 className="font-cairo font-extrabold text-sky-800 text-lg mb-4">{STAGE_LABELS[stage]}</h3>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-5">
              <div className="space-y-1.5">
                <Label>مدة الانتظار بعد التسليم/الإلغاء (دقيقة)</Label>
                <Input type="number" min="1" value={s.cooldown_minutes} onChange={(e) => updateField(stage, "cooldown_minutes", e.target.value)} className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label>منع تكرار استدعاء نفس الطالب (دقيقة)</Label>
                <Input type="number" min="1" value={s.same_student_wait_minutes} onChange={(e) => updateField(stage, "same_student_wait_minutes", e.target.value)} className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label>مدة اعتبار الطلب متأخرًا (دقيقة)</Label>
                <Input type="number" min="1" value={s.late_threshold_minutes} onChange={(e) => updateField(stage, "late_threshold_minutes", e.target.value)} className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label>التأكيد التلقائي للتسليم بعد الخروج (دقيقة، 0 = إيقاف)</Label>
                <Input type="number" min="0" value={s.auto_deliver_minutes ?? 7} onChange={(e) => updateField(stage, "auto_deliver_minutes", e.target.value)} className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label>وقت الإغلاق اليومي (إقفال أي طلب معلّق تلقائيًا)</Label>
                <Input type="time" value={s.daily_cleanup_time || "17:00"} onChange={(e) => updateField(stage, "daily_cleanup_time", e.target.value)} className="h-10" />
              </div>
            </div>

            <div className="text-sm font-cairo font-bold text-slate-700 mb-2">أوقات الاستقبال اليومية</div>
            <div className="grid grid-cols-3 gap-3 px-2.5 mb-1.5">
              <div></div>
              <Label className="text-xs text-slate-500">بداية الاستقبال (فتح الاستدعاء)</Label>
              <Label className="text-xs text-slate-500">نهاية الاستقبال (إغلاق الاستدعاء)</Label>
            </div>
            <div className="space-y-2">
              {DAY_KEYS.map((day) => (
                <div key={day} className="grid grid-cols-3 gap-3 items-center bg-slate-50 rounded-lg p-2.5">
                  <div className="text-sm font-medium text-slate-700">{DAY_LABELS[day]}</div>
                  <Input type="time" value={s.days[day]?.start_time || ""} onChange={(e) => updateDayField(stage, day, "start_time", e.target.value)} className="h-9 bg-white" />
                  <Input type="time" value={s.days[day]?.end_time || ""} onChange={(e) => updateDayField(stage, day, "end_time", e.target.value)} className="h-9 bg-white" />
                </div>
              ))}
            </div>

            <Button onClick={() => saveStage(stage)} disabled={saving[stage]} className="mt-5 h-10 bg-sky-700 hover:bg-sky-800 text-white gap-2">
              {saving[stage] ? <Loader2 className="animate-spin" size={15} /> : <CheckCircle2 size={15} />} حفظ إعدادات {STAGE_LABELS[stage]}
            </Button>
          </Card>
        );
      })}
    </div>
  );
}

const STATUS_LABELS = {
  new: { text: "طلب جديد", color: "text-amber-700 bg-amber-50 border-amber-200" },
  exited: { text: "خرج من الفصل", color: "text-blue-700 bg-blue-50 border-blue-200" },
  on_the_way: { text: "في الطريق", color: "text-blue-700 bg-blue-50 border-blue-200" },
};

function StageFilter({ selected, onToggle }) {
  return (
    <div className="flex flex-wrap gap-3 mb-4 bg-slate-50 rounded-lg p-3 border border-slate-200">
      <span className="text-xs font-cairo font-bold text-slate-600 self-center">عرض المراحل:</span>
      {["primary", "middle", "high"].map((st) => (
        <label key={st} className="flex items-center gap-1.5 text-sm cursor-pointer">
          <input type="checkbox" checked={selected.includes(st)} onChange={() => onToggle(st)} className="w-4 h-4 accent-sky-700" />
          {STAGE_LABELS[st]}
        </label>
      ))}
    </div>
  );
}

function RequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [delivering, setDelivering] = useState(null);
  const [selectedStages, setSelectedStages] = useState(["primary", "middle", "high"]);
  const stagesRef = useRef(selectedStages);
  stagesRef.current = selectedStages;

  const load = async () => {
    try {
      const params = stagesRef.current.length > 0 && stagesRef.current.length < 3
        ? { stages: stagesRef.current.join(",") }
        : {};
      const data = await pickupSchoolBoard(params);
      setRequests(data);
    } catch {
      toast.error("تعذر تحميل الطلبات");
    } finally {
      setLoading(false);
    }
  };

  const toggleStage = (st) => {
    setSelectedStages((prev) => prev.includes(st) ? prev.filter(s => s !== st) : [...prev, st]);
  };

  useEffect(() => { load(); }, [selectedStages]);

  useEffect(() => {
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const doDeliver = async (r) => {
    setDelivering(r.id);
    try {
      await pickupConfirmDelivery(r.id);
      toast.success("تم تأكيد تسليم الطالب");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التنفيذ");
    } finally {
      setDelivering(null);
    }
  };

  const counts = {
    new: requests.filter(r => r.status === "new").length,
    exited: requests.filter(r => r.status === "exited" || r.status === "on_the_way").length,
  };

  return (
    <div>
      <StageFilter selected={selectedStages} onToggle={toggleStage} />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Card className="p-4 border-rose-200 bg-rose-50/40">
          <div className="text-xs text-slate-500">🔴 طلبات جديدة</div>
          <div className="text-2xl font-extrabold text-rose-700 tabular-nums">{counts.new}</div>
        </Card>
        <Card className="p-4 border-blue-200 bg-blue-50/40">
          <div className="text-xs text-slate-500">🔵 خرج من الفصل</div>
          <div className="text-2xl font-extrabold text-blue-700 tabular-nums">{counts.exited}</div>
        </Card>
        <Card className="p-4 border-slate-200">
          <div className="text-xs text-slate-500">إجمالي المعروض</div>
          <div className="text-2xl font-extrabold text-slate-700 tabular-nums">{requests.length}</div>
        </Card>
      </div>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-sky-700" /></div>
      ) : requests.length === 0 ? (
        <div className="py-16 text-center text-slate-400">لا توجد طلبات استدعاء نشطة حاليًا.</div>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => {
            const st = STATUS_LABELS[r.status] || { text: r.status, color: "text-slate-600 bg-slate-50 border-slate-200" };
            return (
              <Card key={r.id} className={`p-4 flex items-center justify-between gap-4 flex-wrap ${r.is_late ? "border-rose-300 bg-rose-50/30" : "border-slate-200"}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-cairo font-bold text-slate-900">{r.student_name}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${st.color}`}>{st.text}</span>
                    {r.is_late && <span className="text-[11px] px-2 py-0.5 rounded-full bg-rose-600 text-white">⚠️ متأخر</span>}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {STAGES[r.stage] || r.stage} — {r.grade} — {r.section} | ولي الأمر: {r.guardian_name} | منذ {r.elapsed_minutes} دقيقة
                  </div>
                  <div className="text-[11px] text-slate-400 tabular-nums mt-0.5">#{r.request_number}</div>
                </div>
                {r.status !== "new" && (
                  <Button onClick={() => doDeliver(r)} disabled={delivering === r.id} className="h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs shrink-0">
                    {delivering === r.id ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />} تأكيد التسليم
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function GuardiansTab() {
  const [guardians, setGuardians] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLink, setShowLink] = useState(null);
  const [nid, setNid] = useState("");
  const [linking, setLinking] = useState(false);
  const [selectedStages, setSelectedStages] = useState(["primary", "middle", "high"]);
  const [showPassword, setShowPassword] = useState(null); // guardian object
  const [newPassword, setNewPassword] = useState("");
  const [resettingPw, setResettingPw] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const load = async () => {
    try {
      const params = {};
      if (selectedStages.length > 0 && selectedStages.length < 3) params.stages = selectedStages.join(",");
      if (searchQuery.trim()) params.q = searchQuery.trim();
      const [data, statsData] = await Promise.all([
        pickupListGuardians(params),
        pickupGuardiansStats({ stages: params.stages }),
      ]);
      setGuardians(data);
      setStats(statsData);
    } catch {
      toast.error("تعذر تحميل أولياء الأمور");
    } finally {
      setLoading(false);
    }
  };

  const toggleStage = (st) => {
    setSelectedStages((prev) => prev.includes(st) ? prev.filter(s => s !== st) : [...prev, st]);
  };

  useEffect(() => { load(); }, [selectedStages]);
  useEffect(() => {
    const timer = setTimeout(() => { load(); }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [searchQuery]);

  const doDeleteGuardian = async (g) => {
    if (!window.confirm(`حذف حساب ولي الأمر "${g.full_name}" وكل روابطه بالأبناء؟`)) return;
    try {
      await pickupDeleteGuardian(g.id);
      toast.success("تم حذف الحساب");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحذف");
    }
  };

  const doUnlink = async (linkId) => {
    if (!window.confirm("إزالة ربط هذا الطالب بولي الأمر؟")) return;
    try {
      await pickupUnlinkStudent(linkId);
      toast.success("تم إزالة الربط");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الإزالة");
    }
  };

  const doAdminLink = async () => {
    if (!nid.trim() || !showLink) return;
    setLinking(true);
    try {
      const res = await pickupAdminLink({ guardian_id: showLink.id, national_id: nid.trim() });
      toast.success(`تم ربط الطالب ${res.student.name} بنجاح`);
      setShowLink(null); setNid("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الربط");
    } finally {
      setLinking(false);
    }
  };

  const doResetPassword = async () => {
    if (!showPassword || newPassword.length < 6) {
      toast.error("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
      return;
    }
    setResettingPw(true);
    try {
      await pickupResetGuardianPassword(showPassword.id, newPassword);
      toast.success(`تم تحديث كلمة مرور ${showPassword.full_name} بنجاح`);
      setShowPassword(null); setNewPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التحديث");
    } finally {
      setResettingPw(false);
    }
  };

  if (loading) return <div className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-sky-700" /></div>;

  return (
    <div>
      <StageFilter selected={selectedStages} onToggle={toggleStage} />
      <div className="relative mb-4">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="بحث برقم هوية الطالب، جوال ولي الأمر، اسم ولي الأمر، أو اسم الطالب..."
          className="pr-9 h-11"
        />
      </div>
      {stats && (
        <div className="grid grid-cols-3 gap-3 mb-5">
          <Card className="p-4 border-sky-200 bg-sky-50/40">
            <div className="text-xs text-slate-500">إجمالي أولياء الأمور</div>
            <div className="text-2xl font-extrabold text-sky-700 tabular-nums">{stats.total}</div>
          </Card>
          <Card className="p-4 border-emerald-200 bg-emerald-50/40">
            <div className="text-xs text-slate-500">مرتبطون بأبناء</div>
            <div className="text-2xl font-extrabold text-emerald-700 tabular-nums">{stats.linked}</div>
          </Card>
          <Card className="p-4 border-amber-200 bg-amber-50/40">
            <div className="text-xs text-slate-500">غير مرتبطين</div>
            <div className="text-2xl font-extrabold text-amber-700 tabular-nums">{stats.unlinked}</div>
          </Card>
        </div>
      )}
      {guardians.length === 0 ? (
        <div className="py-16 text-center text-slate-400">لا يوجد أولياء أمور مطابقون للفلتر المحدد.</div>
      ) : (
        <div className="space-y-3">
          {guardians.map((g) => (
            <Card key={g.id} className="p-4 border-slate-200">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-cairo font-bold text-slate-900">{g.full_name}</div>
                  <div className="text-xs text-slate-500 tabular-nums" dir="ltr">{g.phone}</div>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setShowLink(g)} variant="outline" className="h-8 gap-1.5 text-sky-700 border-sky-200 hover:bg-sky-50 text-xs">
                    <Link2 size={13} /> ربط طالب
                  </Button>
                  <Button onClick={() => { setShowPassword(g); setNewPassword(""); }} variant="outline" className="h-8 gap-1.5 text-amber-700 border-amber-200 hover:bg-amber-50 text-xs">
                    <KeyRound size={13} /> كلمة المرور
                  </Button>
                  <Button onClick={() => doDeleteGuardian(g)} variant="outline" className="h-8 gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50 text-xs">
                    <Trash2 size={13} /> حذف الحساب
                  </Button>
                </div>
              </div>
              {g.children.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {g.children.map((c) => (
                    <span key={c.id} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200">
                      {c.name} ({c.grade})
                      <button onClick={() => doUnlink(c.link_id)} className="text-slate-400 hover:text-rose-600"><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {showLink && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowLink(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-cairo font-bold text-slate-900 mb-3">ربط طالب بحساب {showLink.full_name}</h3>
            <Label className="text-xs">رقم هوية الطالب</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={nid} onChange={(e) => setNid(e.target.value)} placeholder="رقم الهوية" className="h-10" dir="ltr" />
              <Button onClick={doAdminLink} disabled={linking} className="h-10 gap-1.5 bg-sky-700 hover:bg-sky-800 text-white shrink-0">
                {linking ? <Loader2 className="animate-spin" size={15} /> : <Search size={15} />}
              </Button>
            </div>
            <Button variant="outline" onClick={() => setShowLink(null)} className="w-full h-9 mt-3">إغلاق</Button>
          </div>
        </div>
      )}

      {showPassword && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowPassword(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-cairo font-bold text-slate-900 mb-3">تغيير كلمة مرور {showPassword.full_name}</h3>
            <Label className="text-xs">كلمة المرور الجديدة (6 أحرف على الأقل)</Label>
            <Input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="كلمة المرور الجديدة" className="h-10 mt-1.5" dir="ltr" />
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowPassword(null)} className="flex-1 h-10">إلغاء</Button>
              <Button onClick={doResetPassword} disabled={resettingPw} className="flex-1 h-10 bg-amber-600 hover:bg-amber-700 text-white gap-1.5">
                {resettingPw ? <Loader2 className="animate-spin" size={15} /> : <KeyRound size={15} />} تحديث
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
